import { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { SettingsRow } from "~/pages/manifold-nextgen/components/settings/SettingsRow";
import { SettingsSection } from "~/pages/manifold-nextgen/components/settings/SettingsSection";
import { useProjectFundingSource } from "~/pages/manifold-nextgen/hooks/useProjectFundingSource";
import { ChangeFundingSourceDialog } from "./ChangeFundingSourceDialog";

interface FundingSourceSectionProps {
  workbenchId: number | null;
}

interface PendingFundingSource {
  id: string;
  name?: string;
}

const NOT_AVAILABLE_LABEL = "Funding source not available";

export const FundingSourceSection = ({ workbenchId }: FundingSourceSectionProps) => {
  const { enabled, fundingSources, currentFundingSourceId, canEdit, updateFundingSource } =
    useProjectFundingSource(workbenchId);
  const [pending, setPending] = useState<PendingFundingSource | null>(null);

  if (!enabled) {
    return null;
  }

  const currentValue = currentFundingSourceId ?? "";
  const currentIsStale =
    !!currentValue &&
    !fundingSources.some((fundingSource) => String(fundingSource.id) === currentValue);

  return (
    <>
      <SettingsSection title="Cost Attribution">
        <SettingsRow
          title="Funding Source"
          description="Which funding source this project's compute and storage costs are billed to."
        >
          <Select
            value={currentValue}
            disabled={!canEdit}
            onValueChange={(value) => {
              if (value && value !== currentValue) {
                setPending({
                  id: value,
                  name: fundingSources.find((fundingSource) => String(fundingSource.id) === value)
                    ?.name,
                });
              }
            }}
          >
            <SelectTrigger className="w-56">
              <SelectValue placeholder="Select funding source" />
            </SelectTrigger>
            <SelectContent>
              {currentIsStale && (
                <SelectItem value={currentValue} disabled className="hidden">
                  {NOT_AVAILABLE_LABEL}
                </SelectItem>
              )}
              {fundingSources.map((fundingSource) => (
                <SelectItem key={fundingSource.id} value={String(fundingSource.id)}>
                  {fundingSource.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SettingsRow>
      </SettingsSection>
      <ChangeFundingSourceDialog
        open={!!pending}
        onOpenChange={(open) => {
          if (!open) {
            setPending(null);
          }
        }}
        newFundingSourceName={pending?.name}
        onConfirm={async () => {
          if (pending) {
            await updateFundingSource(pending.id);
          }
        }}
      />
    </>
  );
};
